// ─────────────────────────────────────────────────────────────
// ledger.config.js — Reports 5a & 5b: vendor and customer ledgers
// Shape: list (portrait) with an opening row and running balance
//
// One config, two parties. The ledger conventions are mirror
// images and the backend already applies each correctly, so this
// file reads the balance rather than recomputing it — the running
// figure printed is exactly the one shown on screen.
//
//   VENDOR    purchase = credit (we owe more), payment = debit
//             positive balance → CR (we owe them)
//   CUSTOMER  invoice  = debit  (they owe more), payment = credit
//             positive balance → DR (they owe us)
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

const PARTY = {
  vendor: {
    title: 'Vendor Ledger',
    heading: 'Vendor Details',
    debitLabel: 'Payment (Dr)',
    creditLabel: 'Purchase (Cr)',
    // For us, owing a vendor money is the adverse side
    debitTone: 'credit',
    creditTone: 'debit',
    totalDebitLabel: 'Total Payments',
    totalCreditLabel: 'Total Purchases',
  },
  customer: {
    title: 'Customer Ledger',
    heading: 'Customer Details',
    debitLabel: 'Invoice (Dr)',
    creditLabel: 'Receipt (Cr)',
    // A customer owing us is the adverse side
    debitTone: 'debit',
    creditTone: 'credit',
    totalDebitLabel: 'Total Invoiced',
    totalCreditLabel: 'Total Received',
  },
};

export const ledgerConfig = ({ party, data, from_date, to_date }) => {
  const P = PARTY[party] || PARTY.vendor;
  const info = data.customer || data.vendor || {};

  const opening = num(data.opening_balance);
  const closing = num(data.closing_balance);

  const rows = (data.transactions || []).map((t) => ({
    date: t.date,
    voc_no: t.voc_no,
    narration: t.narration,
    debit: num(t.debit),
    credit: num(t.credit),
    // The balance is taken as the backend computed it, not
    // re-derived here — two sources for one number is one too many.
    balance_display: `${FORMATTERS.money(Math.abs(num(t.balance)))} ${
      party === 'vendor'
        ? num(t.balance) >= 0 ? 'CR' : 'DR'
        : num(t.balance) >= 0 ? 'DR' : 'CR'
    }`,
  }));

  const openingRow = {
    __subtotal: true,
    narration: 'Opening Balance',
    balance_display: `${FORMATTERS.money(Math.abs(opening))} ${data.opening_suffix || ''}`,
  };

  const totalDebit = num(data.total_debit);
  const totalCredit = num(data.total_credit);

  return {
    kind: 'list',
    orientation: 'p',
    title: P.title,
    subtitle: `${FORMATTERS.date(from_date)}  to  ${FORMATTERS.date(to_date)}`,

    boxes: [
      {
        heading: P.heading,
        rows: [
          { label: 'Name', value: info.name },
          { label: 'Contact', value: info.contact_person },
          { label: 'Phone', value: info.phone },
          { label: 'Address', value: info.address },
          {
            label: 'NTN / STRN',
            value: `${info.ntn || '—'}  /  ${info.strn || '—'}`,
          },
        ],
      },
      {
        heading: 'Statement Summary',
        rows: [
          {
            label: 'Opening',
            value: `${FORMATTERS.money(Math.abs(opening))} ${data.opening_suffix || ''}`,
            label2: 'Entries',
            value2: rows.length,
          },
          { label: P.totalDebitLabel, value: FORMATTERS.money(totalDebit) },
          { label: P.totalCreditLabel, value: FORMATTERS.money(totalCredit) },
          {
            label: 'Closing',
            value: `${FORMATTERS.money(Math.abs(closing))} ${data.closing_suffix || ''}`,
          },
          {
            label: 'Terms',
            value: info.payment_terms_days
              ? `${info.payment_terms_days} days`
              : 'On delivery',
          },
        ],
      },
    ],

    columns: [
      { key: 'date', label: 'Date', align: 'left', fmt: 'date', width: 24 },
      { key: 'voc_no', label: 'Voucher', align: 'left', fmt: 'text', width: 24 },
      { key: 'narration', label: 'Narration', align: 'left', fmt: 'text' },
      {
        key: 'debit',
        label: P.debitLabel,
        align: 'right',
        fmt: 'money',
        width: 26,
        tone: P.debitTone,
        blankZero: true,
      },
      {
        key: 'credit',
        label: P.creditLabel,
        align: 'right',
        fmt: 'money',
        width: 26,
        tone: P.creditTone,
        blankZero: true,
      },
      {
        key: 'balance_display',
        label: 'Balance',
        align: 'right',
        fmt: 'text',
        width: 30,
        bold: true,
      },
    ],

    rows: [openingRow, ...rows],

    footRow: [
      'TOTAL',
      '',
      '',
      totalDebit ? FORMATTERS.money(totalDebit) : '',
      totalCredit ? FORMATTERS.money(totalCredit) : '',
      `${FORMATTERS.money(Math.abs(closing))} ${data.closing_suffix || ''}`,
    ],

    totals: [
      { label: 'Opening Balance', value: Math.abs(opening), fmt: 'money' },
      { label: P.totalDebitLabel, value: totalDebit, fmt: 'money' },
      { label: P.totalCreditLabel, value: totalCredit, fmt: 'money' },
      {
        label: 'Closing Balance',
        value: `${FORMATTERS.money(Math.abs(closing))} ${data.closing_suffix || ''}`,
        fmt: 'text',
        emphasis: true,
        tone: closing === 0 ? 'net' : party === 'vendor'
          ? (closing > 0 ? 'expense' : 'income')
          : (closing > 0 ? 'expense' : 'income'),
      },
    ],

    signatures: SIGNATURES.report,
  };
};

export default ledgerConfig;